/**
 * /pulse-item/:id — public detail view for a single Neighborhood Pulse post.
 *
 * Renders full title, description (with URLs auto-linkified), image (or a
 * generated icon tile), tags, author, location, and social links.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { ArrowLeft, MapPin, Globe, Youtube, Instagram, Calendar, Loader2, Radio } from "lucide-react";
import Avatar from "@/components/Avatar";
import Linkify from "@/components/Linkify";

const CATEGORY_TILE = {
  event: { color: "#B25D38", Icon: Calendar, label: "EVENT" },
  news:  { color: "#0891B2", Icon: Radio,    label: "NEWS" },
  info:  { color: "#059669", Icon: Radio,    label: "INFO" },
};

function pickTileMeta(item) {
  if (item?.category && CATEGORY_TILE[item.category]) return CATEGORY_TILE[item.category];
  const tags = (item?.tags || []).map((t) => (t || "").toLowerCase());
  if (tags.includes("events")) return CATEGORY_TILE.event;
  if (tags.includes("news")) return CATEGORY_TILE.news;
  return { color: "var(--wofy-primary)", Icon: Radio, label: "PULSE" };
}

export default function PulseItemDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    http.get(`/pulse-items/${id}`)
      .then((r) => setItem(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Post not found."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="pulse-item-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
      </div>
    );
  }
  if (error || !item) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="pulse-item-error">
        <h1 className="font-display text-2xl">{error || "Not found."}</h1>
        <Link to="/pulse" className="wofy-btn-primary mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Back to Neighborhood Pulse
        </Link>
      </div>
    );
  }

  const tile = pickTileMeta(item);
  const TileIcon = tile.Icon;
  const location = [item.city, item.state, item.country].filter(Boolean).join(", ");

  return (
    <article className="max-w-3xl mx-auto px-6 py-10" data-testid={`pulse-item-detail-${item.id}`}>
      <Link to="/pulse" className="wofy-btn-ghost text-xs" data-testid="pulse-item-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Neighborhood Pulse
      </Link>

      <div className="mt-6 wofy-card !p-0 overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.title}
            className="w-full aspect-video object-cover"
            data-testid="pulse-item-image"
          />
        ) : (
          <div
            className="w-full aspect-video flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${tile.color}22, ${tile.color}66)` }}
            data-testid="pulse-item-image-fallback"
          >
            <div className="text-center">
              <TileIcon className="w-16 h-16 mx-auto" style={{ color: tile.color }} />
              <div className="mt-3 font-mono uppercase tracking-widest text-xs" style={{ color: tile.color }}>{tile.label}</div>
            </div>
          </div>
        )}

        <div className="p-6">
          <div className="flex items-center gap-2 text-xs mb-3" style={{ color: "var(--wofy-text-soft)" }}>
            <Avatar src={item.user_avatar} name={item.user_name} size={28} />
            {item.user_handle ? (
              <Link to={`/u/${item.user_handle}`} className="hover:underline font-medium">{item.user_name || "Member"}</Link>
            ) : (
              <span className="font-medium">{item.user_name || "Member"}</span>
            )}
            <span className="opacity-50">·</span>
            <time className="font-mono">{item.created_at ? new Date(item.created_at).toLocaleString() : ""}</time>
          </div>

          <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="pulse-item-title">{item.title}</h1>

          {item.description && (
            <div className="mt-4 text-base whitespace-pre-wrap leading-relaxed" data-testid="pulse-item-description">
              <Linkify text={item.description} />
            </div>
          )}

          {location && (
            <div className="mt-5 inline-flex items-center gap-1 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              <MapPin className="w-4 h-4" /> {location}
            </div>
          )}

          {item.tags?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="pulse-item-tags">
              {item.tags.map((t) => (
                <span key={t} className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>#{t}</span>
              ))}
            </div>
          )}

          {(item.website_url || item.youtube_url || item.instagram_url) && (
            <div className="mt-6 pt-4 border-t flex items-center gap-3 text-sm" style={{ borderColor: "var(--wofy-border)" }}>
              {item.website_url && (
                <a href={item.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: "var(--wofy-primary)" }} data-testid="pulse-item-website-link">
                  <Globe className="w-4 h-4" /> Website
                </a>
              )}
              {item.youtube_url && (
                <a href={item.youtube_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: "#FF0000" }} data-testid="pulse-item-youtube-link">
                  <Youtube className="w-4 h-4" /> YouTube
                </a>
              )}
              {item.instagram_url && (
                <a href={item.instagram_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: "#E4405F" }} data-testid="pulse-item-instagram-link">
                  <Instagram className="w-4 h-4" /> Instagram
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
